import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
} from "@angular/core";
import { environment } from "../../environments/environment";

type PlantGuardianAiMountOptions = {
  basename?: string;
};

type RoleAccessRemoteConfig = {
  remoteName: string;
  routeBasePath: string;
  devModuleUrl?: string;
  scriptUrl: string;
  styleUrl?: string;
};

type RoleAccessRemoteApi = {
  mount: (
    element: HTMLElement,
    options?: PlantGuardianAiMountOptions
  ) => (() => void) | void;
  unmount?: (element?: HTMLElement) => void;
};

declare global {
  interface Window {
    PlantGuardianAI?: RoleAccessRemoteApi;
    __plantGuardianAiRemotePromise__?: Promise<RoleAccessRemoteApi>;
  }
}

@Component({
  selector: "app-role-access-host",
  standalone: false,
  templateUrl: "./plant-guardian-ai-host.component.html",
  styleUrls: ["./plant-guardian-ai-host.component.css"],
})
export class RoleAccessHostComponent implements AfterViewInit, OnDestroy {
  private readonly legacyRouteBasePath = "/plant-guardian-ai";
  private readonly scriptElementId = "role-access-remote-script";
  private readonly styleElementId = "role-access-remote-style";

  @ViewChild("remoteRoot", { static: true })
  remoteRoot!: ElementRef<HTMLDivElement>;

  loading = true;
  error: string | null = null;

  private disposeRemote?: () => void;

  async ngAfterViewInit(): Promise<void> {
    try {
      const remote = await this.loadRemote();
      const dispose = remote.mount(this.remoteRoot.nativeElement, {
        basename: this.getBasename(),
      });

      this.disposeRemote =
        typeof dispose === "function"
          ? dispose
          : () => remote.unmount?.(this.remoteRoot.nativeElement);
    } catch (error) {
      console.error("[PlantGuardianAI] Failed to load remote", error);
      this.error =
        "Plant Guardian AI is unavailable right now. Check the remote bundle deployment and try again.";
    } finally {
      this.loading = false;
    }
  }

  ngOnDestroy(): void {
    this.disposeRemote?.();
    this.disposeRemote = undefined;
    this.resetRemoteRegistration();
    window.__plantGuardianAiRemotePromise__ = undefined;
    this.removeInjectedAsset(this.scriptElementId);
    this.removeInjectedAsset(this.styleElementId);
  }

  private async loadRemote(): Promise<RoleAccessRemoteApi> {
    if (window.PlantGuardianAI) {
      return window.PlantGuardianAI;
    }

    if (!window.__plantGuardianAiRemotePromise__) {
      window.__plantGuardianAiRemotePromise__ = this.shouldUseDevServer()
        ? this.loadDevRemote(environment.reactRemote).catch((error) => {
            console.warn(
              "[PlantGuardianAI] Dev module unavailable, falling back to built asset bundle.",
              error
            );
            this.removeInjectedAsset(this.scriptElementId);
            return this.loadBuiltRemote(environment.reactRemote);
          })
        : this.loadBuiltRemote(environment.reactRemote);
    }

    return window.__plantGuardianAiRemotePromise__;
  }

  private shouldUseDevServer(): boolean {
    return Boolean(
      !environment.production && environment.reactRemote.devModuleUrl
    );
  }

  private getBasename(): string {
    if (
      typeof window !== "undefined" &&
      window.location.pathname.startsWith(this.legacyRouteBasePath)
    ) {
      return this.legacyRouteBasePath;
    }

    return environment.reactRemote.routeBasePath;
  }

  private loadDevRemote(
    remoteConfig: RoleAccessRemoteConfig
  ): Promise<RoleAccessRemoteApi> {
    return new Promise((resolve, reject) => {
      const existingScript = document.getElementById(
        this.scriptElementId
      ) as HTMLScriptElement | null;

      if (existingScript) {
        existingScript.addEventListener("load", () =>
          this.resolveRemoteRegistration(resolve, reject)
        );
        existingScript.addEventListener("error", () =>
          reject(new Error("PlantGuardianAI dev module failed to load."))
        );
        return;
      }

      const script = document.createElement("script");
      script.id = this.scriptElementId;
      script.type = "module";
      script.src = this.withCacheBuster(remoteConfig.devModuleUrl!);
      script.onload = () => this.resolveRemoteRegistration(resolve, reject);
      script.onerror = () =>
        reject(new Error("PlantGuardianAI dev module failed to load."));

      document.body.appendChild(script);
    });
  }

  private loadBuiltRemote(
    remoteConfig: RoleAccessRemoteConfig
  ): Promise<RoleAccessRemoteApi> {
    return new Promise((resolve, reject) => {
      this.ensureStylesheet(remoteConfig);

      const existingScript = document.getElementById(
        this.scriptElementId
      ) as HTMLScriptElement | null;

      if (existingScript) {
        existingScript.addEventListener("load", () =>
          this.resolveRemoteRegistration(resolve, reject)
        );
        existingScript.addEventListener("error", () =>
          reject(new Error("PlantGuardianAI remote script failed to load."))
        );
        return;
      }

      const script = document.createElement("script");
      script.id = this.scriptElementId;
      script.src = this.withCacheBuster(remoteConfig.scriptUrl);
      script.async = true;
      script.onload = () => this.resolveRemoteRegistration(resolve, reject);
      script.onerror = () =>
        reject(new Error("PlantGuardianAI remote script failed to load."));

      document.body.appendChild(script);
    });
  }

  private resolveRemoteRegistration(
    resolve: (
      value: RoleAccessRemoteApi | PromiseLike<RoleAccessRemoteApi>
    ) => void,
    reject: (reason?: unknown) => void
  ): void {
    if (window.PlantGuardianAI) {
      resolve(window.PlantGuardianAI);
      return;
    }

    reject(
      new Error(
        "PlantGuardianAI global was not registered after the script loaded."
      )
    );
  }

  private ensureStylesheet(remoteConfig: RoleAccessRemoteConfig): void {
    if (
      document.getElementById(this.styleElementId) ||
      !remoteConfig.styleUrl
    ) {
      return;
    }

    const link = document.createElement("link");
    link.id = this.styleElementId;
    link.rel = "stylesheet";
    link.href = this.withCacheBuster(remoteConfig.styleUrl);
    document.head.appendChild(link);
  }

  private withCacheBuster(url: string): string {
    if (environment.production) {
      return url;
    }

    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}t=${Date.now()}`;
  }

  private removeInjectedAsset(elementId: string): void {
    document.getElementById(elementId)?.remove();
  }

  private resetRemoteRegistration(): void {
    try {
      window.PlantGuardianAI = undefined;
    } catch {
      // Ignore non-configurable global assignment failures during teardown.
    }
  }
}
